import React, { useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText } from '@mui/material'
import { PatientHistoryType } from 'src/graphql.generated'
import { createTreatmentPlanFormSchema } from 'src/participantHistoryForm/form/TreatmentPlanFormSchema'
import { useLoadedNoteDraft } from 'src/noteDraft/useNoteDraft'
import { NoteDraftAlerts, NoteDraftLoadError } from 'src/noteDraft/NoteDraftAlerts'
import { useDraftSaver, useRestoredDraft, primeRestoredDraft } from '../../../../api/historyDraft'
import { usePatientHistoryPrefill } from '../../../../api/usePatientHistoryPrefill'
import { useSubmitPatientHistory } from '../../../../api/useSubmitPatientHistory'
import { BillingCode } from '../billingCodes'
import BillingCodesSelect from '../BillingCodesSelect'
import {
  FieldSection,
  FormActions,
  LabeledCheckbox,
  LabeledText,
  MissingFields,
  todayIso,
  useRequiredFields,
} from '../../../fieldPrimitives'
import DialogHeader from '../../../../components/DialogHeader'
import { useSelfPay } from '../useSelfPay'
import { layoutForType } from '../../historyLayouts'
import PrefillLoading from '../PrefillLoading'
import { ValidationIssues, validationIssues } from '../validation'
import { boolAt, codesAt, textAt } from '../valuePath'

const TreatmentPlanForm = ({ uid, prefill }: { uid: string; prefill: Record<string, unknown> }) => {
  const restored = useRestoredDraft(uid, PatientHistoryType.TreatmentPlan)
  const plan = 'interventionsAndEngagementPlan'
  const [presentingProblem, setPresentingProblem] = useState(textAt(restored, 'presentingProblem'))
  const [treatmentGoals, setTreatmentGoals] = useState(textAt(restored, 'treatmentGoals'))
  const [billingCodes, setBillingCodes] = useState(codesAt(restored ?? prefill, 'billingCodes') as BillingCode[])
  const [participateTreatmentProgram, setParticipateTreatmentProgram] = useState(
    boolAt(restored, `${plan}.participateTreatmentProgram`, true)
  )
  const [followupTelehealthSessions, setFollowupTelehealthSessions] = useState(
    boolAt(restored, `${plan}.followupTelehealthSessions`, true)
  )
  const [utilizeCommunicationChannels, setUtilizeCommunicationChannels] = useState(
    boolAt(restored, `${plan}.utilizeCommunicationChannels`, true)
  )
  const [participantAgreement, setParticipantAgreement] = useState(
    boolAt(restored, `${plan}.participantAgreement`, true)
  )
  const [additionalResources, setAdditionalResources] = useState(textAt(restored, `${plan}.additionalResources`))
  const [coordinationWithExternalProviders, setCoordinationWithExternalProviders] = useState(
    textAt(restored, `${plan}.coordinationWithExternalProviders`)
  )
  const [other, setOther] = useState(textAt(restored, `${plan}.other`))
  const [obstaclesAndActionPlan, setObstaclesAndActionPlan] = useState(textAt(restored, 'obstaclesAndActionPlan'))
  const [issues, setIssues] = useState<string[]>([])
  const [confirming, setConfirming] = useState(false)
  const { submit, submitting, error } = useSubmitPatientHistory(uid)
  const { isSelfPay } = useSelfPay(uid)

  const required = useRequiredFields({
    'Presenting Problem': Boolean(presentingProblem.trim()),
    'Treatment Goals': Boolean(treatmentGoals.trim()),
    ...(!isSelfPay && { 'Billing codes': billingCodes.length > 0 }),
  })

  const value = {
    presentingProblem,
    treatmentGoals,
    billingCodes,
    interventionsAndEngagementPlan: {
      participateTreatmentProgram,
      followupTelehealthSessions,
      utilizeCommunicationChannels,
      participantAgreement,
      additionalResources,
      coordinationWithExternalProviders,
      other,
    },
    obstaclesAndActionPlan,
  }
  useDraftSaver(uid, PatientHistoryType.TreatmentPlan, value, submitting)

  const save = async () => {
    const found = validationIssues(
      createTreatmentPlanFormSchema({ requireBillingCodes: !isSelfPay }),
      value,
      layoutForType(PatientHistoryType.TreatmentPlan)
    )

    setIssues(found)

    if (found.length > 0) {
      return
    }

    await submit({ date: todayIso(), type: PatientHistoryType.TreatmentPlan, value })
    setPresentingProblem('')
    setTreatmentGoals('')
    setBillingCodes([])
    setAdditionalResources('')
    setCoordinationWithExternalProviders('')
    setOther('')
    setObstaclesAndActionPlan('')
    required.reset()
    setConfirming(false)
  }

  const onSubmit = async () => {
    if (!required.attempt()) {
      return
    }

    if (isSelfPay && billingCodes.length === 0) {
      setConfirming(true)
      return
    }

    await save()
  }

  return (
    <FieldSection>
      <FieldSection title="Treatment Plan">
        <LabeledText
          label="Presenting Problem"
          value={presentingProblem}
          onChange={setPresentingProblem}
          helperText="Briefly summarize the primary reason(s) the individual is seeking treatment at this time and/or the current challenge or change in circumstances that necessitates an adjustment to the treatment plan"
          multiline
          required
          error={required.error('Presenting Problem')}
        />
        <LabeledText
          label="Treatment Goals"
          value={treatmentGoals}
          onChange={setTreatmentGoals}
          helperText="Include at least 1 SMART goal"
          multiline
          required
          error={required.error('Treatment Goals')}
        />
      </FieldSection>

      <FieldSection title="Billing and Diagnosis Codes">
        <BillingCodesSelect
          value={billingCodes}
          onChange={setBillingCodes}
          required={!isSelfPay}
          error={required.error('Billing codes')}
        />
      </FieldSection>

      <FieldSection title="Interventions/ Engagement Plan">
        <LabeledCheckbox
          label="Client will participate in Meru Treatment program including daily lessons, communication with therapist and biofeedback"
          checked={participateTreatmentProgram}
          onChange={setParticipateTreatmentProgram}
        />
        <LabeledCheckbox
          label="Follow up telehealth sessions as needed"
          checked={followupTelehealthSessions}
          onChange={setFollowupTelehealthSessions}
        />
        <LabeledCheckbox
          label="Therapist will utilize in app chat to communicate with participant and escalate to SMS, email or phone calls in order to re-engage client in treatment as needed"
          checked={utilizeCommunicationChannels}
          onChange={setUtilizeCommunicationChannels}
        />
        <LabeledCheckbox
          label="Participant agrees to the discussed Treatment plan"
          checked={participantAgreement}
          onChange={setParticipantAgreement}
        />
        <LabeledText
          label="Additional Resources Provided (include details)"
          value={additionalResources}
          onChange={setAdditionalResources}
          multiline
        />
        <LabeledText
          label="Coordination of care with external providers (include details)"
          value={coordinationWithExternalProviders}
          onChange={setCoordinationWithExternalProviders}
          multiline
        />
        <LabeledText label="Other" value={other} onChange={setOther} multiline />
      </FieldSection>

      <FieldSection title="Obstacles and Action Plan">
        <LabeledText
          label="Obstacles and Action Plan"
          value={obstaclesAndActionPlan}
          onChange={setObstaclesAndActionPlan}
          helperText="Identify obstacles and create an action plan, including steps the participant and the provider will take to overcome obstacles"
          multiline
        />
      </FieldSection>

      <MissingFields labels={required.missing} />
      <ValidationIssues issues={issues} />
      <FormActions onSubmit={onSubmit} disabled={false} submitting={submitting} error={error} />
      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="xs" fullWidth>
        <DialogHeader title="Diagnosis code missing" onClose={() => setConfirming(false)} />
        <DialogContent>
          <DialogContentText>
            Self-pay treatment plans can be saved without a diagnosis/billing code, but the diagnosis is still part of
            the clinical record. Proceed only if this is intentional.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button variant="contained" disabled={submitting} onClick={save}>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </FieldSection>
  )
}

const TreatmentPlanFields = ({ uid }: { uid: string }) => {
  const { loading, value } = usePatientHistoryPrefill(uid, PatientHistoryType.TreatmentPlan)
  const loaded = useLoadedNoteDraft({ uid, type: PatientHistoryType.TreatmentPlan })

  if (loaded.failed) {
    return <NoteDraftLoadError />
  }

  if (loading || !loaded.ready) {
    return <PrefillLoading />
  }

  primeRestoredDraft({ uid, type: PatientHistoryType.TreatmentPlan, value: loaded.draft })

  return (
    <>
      <NoteDraftAlerts uid={uid} formType={PatientHistoryType.TreatmentPlan} />
      <TreatmentPlanForm uid={uid} prefill={value} />
    </>
  )
}

export default TreatmentPlanFields
